import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, Button, Input, Select } from '@tkpod/ui';
import { api } from '@/lib/api';
import { toast } from '@/lib/toast';

interface PricingProfile {
  id?: string;
  strategy?: string;
  baseFee?: string | null;
  pricePerSqIn?: string | null;
  pricePerLinearIn?: string | null;
  pricePerItem?: string | null;
  fixedWidthIn?: string | null;
  minLengthIn?: string | null;
  maxLengthIn?: string | null;
  minPrice?: string | null;
  roundingRule?: string;
  roundingPrecision?: number;
}

const STRATEGY_OPTIONS = [
  { value: 'PRESET_PRICE', label: 'Preset Price' },
  { value: 'LINEAR_IN', label: 'Linear Inch' },
  { value: 'AREA', label: 'Area (sq in)' },
  { value: 'APPAREL_COMPOSITE', label: 'Apparel Composite' },
];

const ROUNDING_OPTIONS = [
  { value: 'NONE', label: 'None' },
  { value: 'ROUND_UP', label: 'Round Up' },
  { value: 'ROUND_DOWN', label: 'Round Down' },
  { value: 'ROUND_NEAREST', label: 'Round Nearest' },
];

export function CatalogPricingTab({ templateId, pricingProfile, onDirtyChange }: { templateId: string; pricingProfile: Record<string, unknown> | null; onDirtyChange?: (dirty: boolean) => void }) {
  const queryClient = useQueryClient();
  const pp = pricingProfile as PricingProfile | null;
  const [editing, setEditing] = useState(false);
  const setEditingWithDirty = (value: boolean) => {
    setEditing(value);
    onDirtyChange?.(value);
  };
  const [strategy, setStrategy] = useState(pp?.strategy ?? 'PRESET_PRICE');
  const [baseFee, setBaseFee] = useState(pp?.baseFee ?? '');
  const [pricePerSqIn, setPricePerSqIn] = useState(pp?.pricePerSqIn ?? '');
  const [pricePerLinearIn, setPricePerLinearIn] = useState(pp?.pricePerLinearIn ?? '');
  const [pricePerItem, setPricePerItem] = useState(pp?.pricePerItem ?? '');
  const [fixedWidthIn, setFixedWidthIn] = useState(pp?.fixedWidthIn ?? '');
  const [minLengthIn, setMinLengthIn] = useState(pp?.minLengthIn ?? '');
  const [maxLengthIn, setMaxLengthIn] = useState(pp?.maxLengthIn ?? '');
  const [minPrice, setMinPrice] = useState(pp?.minPrice ?? '');
  const [roundingRule, setRoundingRule] = useState(pp?.roundingRule ?? 'NONE');
  const [roundingPrecision, setRoundingPrecision] = useState(pp?.roundingPrecision != null ? String(pp.roundingPrecision) : '2');

  const updateMutation = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      api.patch(`/api/v1/admin/catalog/templates/${templateId}/pricing`, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['catalog-template', templateId] });
      setEditingWithDirty(false);
      toast('Pricing profile updated.');
    },
    onError: (err: Error) => {
      toast(err.message || 'Failed to update pricing.', 'error');
    },
  });

  const handleSave = () => {
    const body: Record<string, unknown> = { strategy, roundingRule };
    if (baseFee) body.baseFee = parseFloat(baseFee as string);
    if (pricePerSqIn) body.pricePerSqIn = parseFloat(pricePerSqIn as string);
    if (pricePerLinearIn) body.pricePerLinearIn = parseFloat(pricePerLinearIn as string);
    if (pricePerItem) body.pricePerItem = parseFloat(pricePerItem as string);
    if (fixedWidthIn) body.fixedWidthIn = parseFloat(fixedWidthIn as string);
    if (minLengthIn) body.minLengthIn = parseFloat(minLengthIn as string);
    if (maxLengthIn) body.maxLengthIn = parseFloat(maxLengthIn as string);
    if (minPrice) body.minPrice = parseFloat(minPrice as string);
    if (roundingPrecision) body.roundingPrecision = parseInt(roundingPrecision);
    updateMutation.mutate(body);
  };

  return (
    <Card>
      <div className="px-6 py-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-base font-semibold text-text-primary">Pricing Profile</h3>
          {!editing ? (
            <Button size="sm" variant="outline" onClick={() => setEditingWithDirty(true)}>Edit</Button>
          ) : (
            <div className="flex gap-2">
              <Button size="sm" onClick={handleSave} disabled={updateMutation.isPending}>
                {updateMutation.isPending ? 'Saving...' : 'Save'}
              </Button>
              <Button size="sm" variant="outline" onClick={() => {
                setStrategy(pp?.strategy ?? 'PRESET_PRICE');
                setBaseFee(pp?.baseFee ?? '');
                setPricePerSqIn(pp?.pricePerSqIn ?? '');
                setPricePerLinearIn(pp?.pricePerLinearIn ?? '');
                setPricePerItem(pp?.pricePerItem ?? '');
                setFixedWidthIn(pp?.fixedWidthIn ?? '');
                setMinLengthIn(pp?.minLengthIn ?? '');
                setMaxLengthIn(pp?.maxLengthIn ?? '');
                setMinPrice(pp?.minPrice ?? '');
                setRoundingRule(pp?.roundingRule ?? 'NONE');
                setRoundingPrecision(pp?.roundingPrecision != null ? String(pp.roundingPrecision) : '2');
                setEditingWithDirty(false);
              }}>Cancel</Button>
            </div>
          )}
        </div>

        {updateMutation.isError && (
          <div className="mb-4 rounded-md bg-danger-50 p-3 text-sm text-danger-700">
            {(updateMutation.error as Error)?.message || 'Failed to update pricing.'}
          </div>
        )}

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div>
            <label className="text-xs font-medium text-text-secondary">Strategy</label>
            {editing ? (
              <Select value={strategy} onChange={(e) => setStrategy(e.target.value)} options={STRATEGY_OPTIONS} />
            ) : (
              <p className="mt-0.5 text-sm text-text-primary">{pp?.strategy ?? 'Not set'}</p>
            )}
          </div>

          <div>
            <label className="text-xs font-medium text-text-secondary">Base Fee</label>
            {editing ? (
              <Input type="number" step="0.01" value={baseFee as string} onChange={(e) => setBaseFee(e.target.value)} />
            ) : (
              <p className="mt-0.5 text-sm text-text-primary">{pp?.baseFee ? `$${pp.baseFee}` : '—'}</p>
            )}
          </div>

          <div>
            <label className="text-xs font-medium text-text-secondary">Price per sq in</label>
            {editing ? (
              <Input type="number" step="0.001" value={pricePerSqIn as string} onChange={(e) => setPricePerSqIn(e.target.value)} />
            ) : (
              <p className="mt-0.5 text-sm text-text-primary">{pp?.pricePerSqIn ? `$${pp.pricePerSqIn}` : '—'}</p>
            )}
          </div>

          <div>
            <label className="text-xs font-medium text-text-secondary">Price per linear in</label>
            {editing ? (
              <Input type="number" step="0.001" value={pricePerLinearIn as string} onChange={(e) => setPricePerLinearIn(e.target.value)} />
            ) : (
              <p className="mt-0.5 text-sm text-text-primary">{pp?.pricePerLinearIn ? `$${pp.pricePerLinearIn}` : '—'}</p>
            )}
          </div>

          <div>
            <label className="text-xs font-medium text-text-secondary">Price per item</label>
            {editing ? (
              <Input type="number" step="0.01" value={pricePerItem as string} onChange={(e) => setPricePerItem(e.target.value)} />
            ) : (
              <p className="mt-0.5 text-sm text-text-primary">{pp?.pricePerItem ? `$${pp.pricePerItem}` : '—'}</p>
            )}
          </div>

          <div>
            <label className="text-xs font-medium text-text-secondary">Fixed Width (in)</label>
            {editing ? (
              <Input type="number" step="0.01" value={fixedWidthIn as string} onChange={(e) => setFixedWidthIn(e.target.value)} />
            ) : (
              <p className="mt-0.5 text-sm text-text-primary">{pp?.fixedWidthIn ? `${pp.fixedWidthIn}"` : '—'}</p>
            )}
          </div>

          <div>
            <label className="text-xs font-medium text-text-secondary">Min Length (in)</label>
            {editing ? (
              <Input type="number" step="0.01" value={minLengthIn as string} onChange={(e) => setMinLengthIn(e.target.value)} />
            ) : (
              <p className="mt-0.5 text-sm text-text-primary">{pp?.minLengthIn ? `${pp.minLengthIn}"` : '—'}</p>
            )}
          </div>

          <div>
            <label className="text-xs font-medium text-text-secondary">Max Length (in)</label>
            {editing ? (
              <Input type="number" step="0.01" value={maxLengthIn as string} onChange={(e) => setMaxLengthIn(e.target.value)} />
            ) : (
              <p className="mt-0.5 text-sm text-text-primary">{pp?.maxLengthIn ? `${pp.maxLengthIn}"` : '—'}</p>
            )}
          </div>

          <div>
            <label className="text-xs font-medium text-text-secondary">Min Price</label>
            {editing ? (
              <Input type="number" step="0.01" value={minPrice as string} onChange={(e) => setMinPrice(e.target.value)} />
            ) : (
              <p className="mt-0.5 text-sm text-text-primary">{pp?.minPrice ? `$${pp.minPrice}` : '—'}</p>
            )}
          </div>

          <div>
            <label className="text-xs font-medium text-text-secondary">Rounding Rule</label>
            {editing ? (
              <Select value={roundingRule} onChange={(e) => setRoundingRule(e.target.value)} options={ROUNDING_OPTIONS} />
            ) : (
              <p className="mt-0.5 text-sm text-text-primary">{pp?.roundingRule ?? 'Not set'}</p>
            )}
          </div>

          <div>
            <label className="text-xs font-medium text-text-secondary">Rounding Precision</label>
            {editing ? (
              <Input type="number" min="0" max="4" step="1" value={roundingPrecision} onChange={(e) => setRoundingPrecision(e.target.value)} />
            ) : (
              <p className="mt-0.5 text-sm text-text-primary">{pp?.roundingPrecision != null ? pp.roundingPrecision : '—'}</p>
            )}
          </div>
        </div>

        {!pp && !editing && (
          <div className="mt-6 rounded-lg border border-dashed border-border p-8 text-center">
            <svg className="mx-auto h-10 w-10 text-text-tertiary/50" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v12m-3-2.818l.879.659c1.171.879 3.07.879 4.242 0 1.172-.879 1.172-2.303 0-3.182C13.536 12.219 12.768 12 12 12c-.725 0-1.45-.22-2.003-.659-1.106-.879-1.106-2.303 0-3.182s2.9-.879 4.006 0l.415.33M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p className="mt-3 text-sm font-medium text-text-secondary">Pricing profile not configured</p>
            <p className="mt-1 text-xs text-text-tertiary">Click Edit to set up pricing for this template.</p>
          </div>
        )}
      </div>
    </Card>
  );
}
